/**
 * pushNotificationService.ts
 * Handles browser Web Push subscription for TrackerV2 chat notifications.
 */

const API_BASE = 'https://www.trackerv2.dev/api';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const arr = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    arr[i] = rawData.charCodeAt(i);
  }
  return arr;
}

async function getVapidPublicKey(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/push/vapid-public-key`);
    if (!res.ok) return null;
    const json = await res.json();
    return json?.data?.publicKey || null;
  } catch {
    return null;
  }
}

/**
 * Register service worker and subscribe to Web Push.
 * Call this after the user logs in.
 */
export async function subscribeToPushNotifications(token: string): Promise<void> {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('Web Push not supported in this browser');
      return;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('Push notification permission denied');
      return;
    }

    // Register service worker
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;

    // Get VAPID key from backend
    const vapidKey = await getVapidPublicKey();
    if (!vapidKey) {
      console.warn('VAPID key not available – push disabled');
      return;
    }

    // Check if already subscribed with the same endpoint
    const existingSub = await registration.pushManager.getSubscription();
    if (existingSub) {
      // Re-send to backend in case it was lost
      await sendSubscriptionToBackend(existingSub, token);
      return;
    }

    // Subscribe
    const applicationServerKey = urlBase64ToUint8Array(vapidKey);
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey.buffer.slice(
        applicationServerKey.byteOffset,
        applicationServerKey.byteOffset + applicationServerKey.byteLength
      ) as ArrayBuffer,
    });

    await sendSubscriptionToBackend(subscription, token);
    console.log('✅ Web Push subscription registered');
  } catch (err) {
    console.error('Failed to subscribe to push notifications:', err);
  }
}

async function sendSubscriptionToBackend(
  subscription: PushSubscription,
  token: string
): Promise<void> {
  const subJson = subscription.toJSON();
  await fetch(`${API_BASE}/push/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      endpoint: subJson.endpoint,
      keys: subJson.keys,
      userAgent: navigator.userAgent,
    }),
  });
}

/**
 * Unsubscribe from Web Push (on logout).
 */
export async function unsubscribeFromPushNotifications(token: string): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) return;

    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) return;

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;

    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();

    await fetch(`${API_BASE}/push/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ endpoint }),
    });

    console.log('🔕 Web Push unsubscribed');
  } catch (err) {
    console.error('Failed to unsubscribe from push:', err);
  }
}
